import { useMemo } from 'react';
import { useSelector } from 'react-redux';

/**
 * Hook to determine recording permissions based on the new backend logic
 * Matches the entitlement system from the backend RecordingController
 */
export const useRecordingPermissions = (recording) => {
  const user = useSelector((state) => state.auth.user);

  return useMemo(() => {
    if (!user || !recording) {
      return {
        canView: false,
        canPurchase: false,
        canDownload: false,
        canRename: false,
        canDelete: false,
        canTrimCrop: false,
        isOwner: false,
        isAdmin: false,
        isSiteOwner: false,
        isEntitled: false,
        shouldShowPurchaseButton: false,
        shouldShowWatchButton: false,
        shouldShowDownloadButton: false
      };
    }

    const userRoles = user.roles || [];
    const isAdmin = userRoles.includes('ADMIN');
    const isSiteOwner = userRoles.includes('SITE_OWNER');
    const isOwner = recording.ownerId === user.id;
    const hasPurchased = recording.purchased;

    // Backend entitlement logic (matching RecordingController):
    // - Admin can do everything (no purchase needed, always entitled)
    // - Owner can manage their own recordings (no purchase needed, always entitled)
    // - Purchasers can view/download
    // Note: Admins and owners are ALWAYS entitled regardless of recording.purchased status
    const isEntitled = isAdmin || isOwner || hasPurchased;

    // Debug logging for permission calculations
    console.log('🔍 useRecordingPermissions - Debug:', {
      recordingId: recording.id,
      userId: user.id,
      userRoles,
      isAdmin,
      isSiteOwner,
      isOwner,
      hasPurchased,
      isEntitled,
      recordingOwnerId: recording.ownerId
    });

    // Permission calculations (matching backend logic)
    const canView = isEntitled;
    const canPurchase = !isEntitled; // Only non-entitled users need to purchase (admins/owners get FREE_GRANTED)
    const canDownload = isEntitled;
    const canRename = isAdmin || isOwner;
    const canDelete = isAdmin; // Only admin can delete
    const canTrimCrop = isAdmin || isSiteOwner; // Backend requires ADMIN or SITE_OWNER

    // UI state calculations
    const shouldShowPurchaseButton = canPurchase;
    const shouldShowWatchButton = canView;
    const shouldShowDownloadButton = canDownload;

    return {
      // Core permissions
      canView,
      canPurchase,
      canDownload,
      canRename,
      canDelete,
      canTrimCrop,
      
      // User role flags
      isOwner,
      isAdmin,
      isSiteOwner,
      isEntitled,
      
      // UI state flags
      shouldShowPurchaseButton,
      shouldShowWatchButton,
      shouldShowDownloadButton,
      
      // Helper for getting action button props
      getActionButtonProps: () => {
        if (shouldShowPurchaseButton) {
          return {
            label: 'Purchase',
            variant: 'primary',
            action: 'purchase'
          };
        } else if (shouldShowWatchButton) {
          return {
            label: 'Watch',
            variant: 'primary',
            action: 'watch'
          };
        } else {
          return {
            label: 'No Access',
            variant: 'disabled',
            action: 'none'
          };
        }
      }
    };
  }, [user, recording]);
};

export default useRecordingPermissions;